import type { DrawingEntity, Point, SourceRef } from '../contract/types.js';
import { normaliseDegrees } from '../transform/matrix.js';

/**
 * Reading a DXF back into Drawing2D entities.
 *
 * This exists so stage 2 of the accuracy pipeline — `drawing.json` against the
 * DXF written from it — can be reported as a per-type figure rather than
 * asserted by a pass/fail test. It parses the file by group code and shares no
 * code with {@link writeDxf}, so a serialisation defect surfaces as a missing,
 * extra or misplaced entity instead of as a writer agreeing with itself.
 *
 * **What this does not prove, stated because the number would otherwise be read
 * as proving it.** The page-to-drawing transform is applied by the writer and by
 * the acceptance runner through the same `toDrawingSpace`, so a defect in that
 * transform moves both sides of the stage 2 comparison together and stays
 * invisible here. `kernel/tests/test_round_trip.py` re-derives the transform in
 * plain Python and compares in world coordinates at 1e-6; that is the
 * independent check and it is why it stays in `verify:all`.
 *
 * **Nothing is dropped silently.** An entity type this reader has no case for is
 * counted by name in {@link DxfReadBack.unsupported} rather than skipped, on the
 * same principle as the operator ledger: a reader that quietly ignores what it
 * cannot parse reports a perfect score for a file it only partly read.
 */

const NO_SOURCE: SourceRef = { pageIndex: 0, operatorIndex: -1, bbox: [0, 0, 0, 0] };

export interface DxfReadBack {
  /** Everything the reader recognised, in the order the file lists it. */
  entities: DrawingEntity[];
  /** DXF type names in the ENTITIES section this reader has no case for, counted. */
  unsupported: Record<string, number>;
}

interface Tag {
  code: number;
  value: string;
}

/**
 * A DXF is a flat sequence of (group code, value) line pairs. Leading and
 * trailing space around the code is legal, so the code is trimmed before it is
 * parsed; the value is taken verbatim, because a text group's own spaces are
 * part of the string.
 */
function scanTags(dxf: string): Tag[] {
  const lines = dxf.split(/\r\n|\r|\n/);
  const tags: Tag[] = [];
  for (let i = 0; i + 1 < lines.length; i += 2) {
    const code = Number.parseInt(lines[i]!.trim(), 10);
    if (!Number.isFinite(code)) continue;
    tags.push({ code, value: lines[i + 1]! });
  }
  return tags;
}

/**
 * The tag runs of the ENTITIES section, one array per entity.
 *
 * Section boundaries are honoured rather than assumed away: `*Model_Space` and
 * `*Paper_Space` are written as `BLOCK`/`ENDBLK` records in the BLOCKS section,
 * and a scanner that collects every `0` group anywhere in the file picks them up
 * as drawing content.
 */
function entityRecords(tags: readonly Tag[]): Tag[][] {
  const records: Tag[][] = [];
  let inEntities = false;
  let current: Tag[] | null = null;

  const close = (): void => {
    if (current) records.push(current);
    current = null;
  };

  for (let i = 0; i < tags.length; i++) {
    const tag = tags[i]!;
    if (tag.code !== 0) {
      if (current) current.push(tag);
      continue;
    }

    if (tag.value === 'SECTION') {
      close();
      const name = tags[i + 1];
      inEntities = name?.code === 2 && name.value === 'ENTITIES';
      continue;
    }
    if (tag.value === 'ENDSEC' || tag.value === 'EOF') {
      close();
      inEntities = false;
      continue;
    }
    if (!inEntities) continue;

    close();
    current = [tag];
  }

  close();
  return records;
}

function first(record: readonly Tag[], code: number): string | undefined {
  return record.find((t) => t.code === code)?.value;
}

function numberAt(record: readonly Tag[], code: number, fallback = Number.NaN): number {
  const raw = first(record, code);
  if (raw === undefined) return fallback;
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) ? value : fallback;
}

/** DXF lineweights are hundredths of a millimetre; -3 means "by layer". */
function strokeWidthOf(record: readonly Tag[]): number {
  const hundredths = numberAt(record, 370, 0);
  return hundredths > 0 ? hundredths / 100 : 0;
}

/** Group 420 is a packed 24-bit true colour; the contract stores six hex digits. */
function colourOf(record: readonly Tag[]): string {
  const packed = numberAt(record, 420, 0);
  return (Math.trunc(packed) & 0xffffff).toString(16).padStart(6, '0');
}

/**
 * Vertices and bulges have to be read positionally.
 *
 * Group 42 modifies the segment leaving the vertex whose 10/20 pair precedes
 * it, and it is omitted entirely when the bulge is zero. Collecting every 42 in
 * the record as a flat list therefore cannot tell one rounded corner from
 * another — the arcs land on the wrong corners and the shape is still a closed
 * polyline of the right size.
 */
function polylinePoints(record: readonly Tag[]): { vertices: Point[]; bulges: number[] } {
  const vertices: Point[] = [];
  const bulges: number[] = [];

  for (const tag of record) {
    if (tag.code === 10) {
      vertices.push({ x: Number.parseFloat(tag.value), y: Number.NaN });
      bulges.push(0);
    } else if (tag.code === 20 && vertices.length > 0) {
      vertices[vertices.length - 1]!.y = Number.parseFloat(tag.value);
    } else if (tag.code === 42 && bulges.length > 0) {
      bulges[bulges.length - 1] = Number.parseFloat(tag.value);
    }
  }

  return { vertices, bulges };
}

/**
 * MTEXT text is spread over group 3 chunks with group 1 holding the tail.
 *
 * Reading group 1 alone returns the last 250 bytes of the string, which looks
 * like text and is why this is a real check rather than a formality: a Japanese
 * note costs three bytes a character and passes 250 after about eighty of them.
 */
function mtextString(record: readonly Tag[]): string {
  let raw = '';
  for (const tag of record) {
    if (tag.code === 3) raw += tag.value;
    else if (tag.code === 1) raw += tag.value;
  }
  return unescapeMText(raw);
}

/**
 * Undoes the writer's four escapes, left to right so a backslash can never be
 * read as introducing the sequence that a preceding backslash already escaped.
 * Any other MTEXT formatting code is passed through untouched: this writer does
 * not emit them, and inventing an interpretation would be reading meaning into a
 * file that does not carry it.
 */
function unescapeMText(raw: string): string {
  let out = '';
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i]!;
    if (ch !== '\\' || i + 1 >= raw.length) {
      out += ch;
      continue;
    }
    const next = raw[i + 1]!;
    if (next === 'P') {
      out += '\n';
      i += 1;
    } else if (next === '\\' || next === '{' || next === '}') {
      out += next;
      i += 1;
    } else {
      out += ch;
    }
  }
  return out;
}

/**
 * Group 40 is a cap height and the contract carries an em, so reading one back
 * undoes the ratio the writer applied. Without this, stage 2 compares a cap
 * against an em and every text entity looks like a near miss on a perfect file.
 */
const CAP_HEIGHT_RATIO = 0.716;

/** Group 71's top-left corner, the only attachment this writer emits. */
const MTEXT_TOP_LEFT = 1;

/** Group 72 = 5 on a TEXT: the string is fitted between groups 10/20 and 11/21. */
const FIT_BETWEEN_POINTS = 5;

/**
 * A DXF reader's default line spacing: 3-on-5, i.e. 5/3 of the cap height.
 *
 * Restated here rather than imported from the writer, on the same rule as
 * `CAP_HEIGHT_RATIO` above: this module exists to disagree with the writer, and a
 * shared constant is a shared assumption.
 */
const THREE_ON_FIVE = 1.667;

/** Group 73 = 2: the line spacing is exactly what group 44 states. */
const EXACT_LINE_SPACING = 2;

/**
 * The first line's baseline, from the corner group 71 says the insertion point is.
 *
 * Group 71 states WHICH CORNER of the text box groups 10/20 describe, and this
 * reader used to ignore it and take that point as the block's anchor. The writer
 * declared bottom-left and supplied the first line's baseline, so the two were
 * wrong in the same direction and stage 2 read MTEXT at 1.000 on files whose
 * notes sat a line too high in any real reader. Independence of implementation
 * is not independence of interpretation.
 *
 * Only **top left** is inverted, and returns null otherwise. Bottom- and
 * middle-anchored boxes need the line count *and* the pitch the reader chose for
 * them, and the file states neither — so there is nothing to invert, and saying
 * so is the honest answer.
 */
function baselineOfMText(
  insert: Point,
  attachment: number,
  height: number,
  rotationDeg: number,
): Point | null {
  if (attachment !== MTEXT_TOP_LEFT) return null;
  // Undoing `anchor + height * (-sin, cos)`: the top of the first line is one
  // text height above its baseline along the text's own up direction.
  const rad = (rotationDeg * Math.PI) / 180;
  return { x: insert.x + Math.sin(rad) * height, y: insert.y - Math.cos(rad) * height };
}

/**
 * MTEXT rotation comes out of the 11/21 direction vector.
 *
 * The writer avoids group 50 on MTEXT deliberately — it is documented as radians
 * and read as degrees by common implementations — so the x-axis vector is the
 * only statement of rotation in the record.
 */
function mtextRotation(record: readonly Tag[]): number {
  const x = numberAt(record, 11, 1);
  const y = numberAt(record, 21, 0);
  if (x === 0 && y === 0) return 0;
  return normaliseDegrees((Math.atan2(y, x) * 180) / Math.PI);
}

export function readDxf(dxf: string): DxfReadBack {
  const entities: DrawingEntity[] = [];
  const unsupported: Record<string, number> = {};

  for (const record of entityRecords(scanTags(dxf))) {
    const type = record[0]!.value;
    const base = {
      id: `dxf-${first(record, 5) ?? String(entities.length + 1)}`,
      layer: first(record, 8) ?? '0',
      strokeWidth: strokeWidthOf(record),
      colour: colourOf(record),
      source: NO_SOURCE,
      origin: 'vector' as const,
    };

    switch (type) {
      case 'LINE':
        entities.push({
          ...base,
          type: 'LINE',
          start: { x: numberAt(record, 10), y: numberAt(record, 20) },
          end: { x: numberAt(record, 11), y: numberAt(record, 21) },
        });
        break;

      case 'LWPOLYLINE': {
        const { vertices, bulges } = polylinePoints(record);
        entities.push({
          ...base,
          type: 'LWPOLYLINE',
          vertices,
          bulges,
          closed: (numberAt(record, 70, 0) & 1) === 1,
        });
        break;
      }

      case 'CIRCLE':
        entities.push({
          ...base,
          type: 'CIRCLE',
          centre: { x: numberAt(record, 10), y: numberAt(record, 20) },
          radius: numberAt(record, 40),
        });
        break;

      case 'ARC':
        entities.push({
          ...base,
          type: 'ARC',
          centre: { x: numberAt(record, 10), y: numberAt(record, 20) },
          radius: numberAt(record, 40),
          startAngle: normaliseDegrees(numberAt(record, 50, 0)),
          endAngle: normaliseDegrees(numberAt(record, 51, 0)),
        });
        break;

      case 'TEXT': {
        /*
          Groups 72 and 73 say which point 10/20 IS — horizontal and vertical
          justification. Only left/baseline leaves the primary point on the
          baseline; anything else moves it to a box corner or a centre and puts
          the alignment point in 11/21 instead.

          This writer only ever emits 0/0, and taking that on trust is exactly
          how the MTEXT attachment point went unnoticed through three
          implementations that shared no code. Checked rather than assumed, for
          the same reason and by the same rule: what cannot be inverted is
          counted as unread.
        */
        const halign = numberAt(record, 72, 0);
        const valign = numberAt(record, 73, 0);
        /*
          Left (0) and Fit (5) both leave groups 10/20 on the baseline at the
          start of the run, which is what makes them readable. Fit additionally
          says the string is set to land on groups 11/21, so the writer can pin
          the advance the PDF measured without knowing the reader's font.

          Every other justification moves the primary point to a box corner or a
          centre, and recovering a baseline from one needs the ascent and descent
          of the font the reader chose. A DXF states neither, so those are counted
          as unread rather than guessed at.
        */
        const fitted = halign === FIT_BETWEEN_POINTS;
        if ((halign !== 0 && !fitted) || valign !== 0) {
          const key = `TEXT(halign=${halign},valign=${valign})`;
          unsupported[key] = (unsupported[key] ?? 0) + 1;
          break;
        }
        const anchor = { x: numberAt(record, 10), y: numberAt(record, 20) };
        const rotation = normaliseDegrees(numberAt(record, 50, 0));
        /*
          A fitted string says the same thing twice: group 50 gives the baseline's
          direction and groups 11/21 give a point along it. Checked rather than
          taken on trust, by the rule the attachment-point defect established --
          three implementations agreed to 1e-6 on a shared misunderstanding, so
          what a file states redundantly is worth reading redundantly.

          A disagreement here is a string running in a direction its own angle
          denies, which no reader could place. Counted as unread.
        */
        if (fitted) {
          const to = { x: numberAt(record, 11), y: numberAt(record, 21) };
          const along = Math.hypot(to.x - anchor.x, to.y - anchor.y);
          const rad = (rotation * Math.PI) / 180;
          const strayed = Math.hypot(
            to.x - (anchor.x + Math.cos(rad) * along),
            to.y - (anchor.y + Math.sin(rad) * along),
          );
          // Generous next to the 1e-6 mm the round trip is held to, because this
          // is a consistency check on a file, not a measurement of one.
          if (along <= 0 || strayed > 1e-6 * Math.max(1, along)) {
            const key = 'TEXT(fit=inconsistent)';
            unsupported[key] = (unsupported[key] ?? 0) + 1;
            break;
          }
        }
        entities.push({
          ...base,
          type: 'TEXT',
          text: first(record, 1) ?? '',
          anchor,
          height: numberAt(record, 40) / CAP_HEIGHT_RATIO,
          rotation,
        });
        break;
      }

      case 'MTEXT': {
        // Group 40 as written is the cap height; the contract carries the em.
        const cap = numberAt(record, 40);
        const height = cap / CAP_HEIGHT_RATIO;
        const rotation = mtextRotation(record);
        const attachment = numberAt(record, 71, MTEXT_TOP_LEFT);
        const baseline = baselineOfMText(
          { x: numberAt(record, 10), y: numberAt(record, 20) },
          attachment,
          // The writer offset by one CAP height, so that is what is undone here.
          cap,
          rotation,
        );
        if (baseline === null) {
          // Counted, never skipped — the same rule as an entity type with no case
          // here. An attachment this reader cannot undo is an entity it did not
          // read, and answering with the insertion point regardless is what made
          // the old writer/reader agreement look like a measurement.
          const key = `MTEXT(attachment=${attachment})`;
          unsupported[key] = (unsupported[key] ?? 0) + 1;
          break;
        }
        /*
          The pitch a CAD application will apply, worked back out of the file.

          Group 44 is a factor of 3-on-5 CAP heights and group 73 says whether it
          is exact. Only the exact style is read: `AtLeast` lets a reader open the
          spacing up for a tall glyph, so what the file states is then a floor
          rather than the pitch, and reporting a floor as a measurement is the
          error this whole class of defect is made of.
        */
        const spacingStyle = numberAt(record, 73, 0);
        const linePitch =
          spacingStyle === EXACT_LINE_SPACING
            ? cap * THREE_ON_FIVE * numberAt(record, 44, 1)
            : undefined;
        entities.push({
          ...base,
          type: 'MTEXT',
          text: mtextString(record),
          anchor: baseline,
          height,
          rotation,
          ...(linePitch !== undefined ? { linePitch } : {}),
        });
        break;
      }

      default:
        unsupported[type] = (unsupported[type] ?? 0) + 1;
    }
  }

  return { entities, unsupported };
}
